import { ChangeEvent, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { createPortal } from "react-dom";

import {
	faChevronDown,
	faChevronUp,
	faList,
	faSearch,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { debounce } from "@mui/material";

import { Spinner } from "phosphor-react";

import { Icon, Loader } from "@/components";
import { classNames } from "@/utils/classNames";
import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { List } from "../../schema/list";

import { ListPickerRow } from "./ListPickerRow";

const PANEL_WIDTH = 400;
const ARCHIVED_FETCH_LIMIT = 100;

export interface ListPickerDropdownProps {
	data: List[];
	label?: string;
	selectedItem: string;
	handleSelect: (value?: Partial<List>) => void;
	handleSearch?: (value: string) => void;
	handleFetchMore: () => void;
	isFetching: boolean;
	isLoading?: boolean;
	isListLoading?: boolean;
	currentPage: number;
	totalPages: number;
	testId?: string;
}

/**
 * Top list-switcher on the List Detail page, styled as the "Picker" mockup: a trigger
 * (list icon + name + chevron) and a 400px portal panel with search-only header, instant
 * single-select rows, and an expandable Archived section. No footer.
 */
export const ListPickerDropdown: FunctionComponent<ListPickerDropdownProps> = ({
	data,
	label,
	selectedItem,
	handleSelect,
	handleSearch,
	handleFetchMore,
	isFetching,
	isLoading,
	isListLoading,
	currentPage,
	totalPages,
	testId,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, opts?: Record<string, unknown>) => t(`list.listPicker.${key}`, opts),
		[t]
	);

	const [showDropdown, setShowDropdown] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [archivedExpanded, setArchivedExpanded] = useState(false);
	const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

	const triggerRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(panelRef, showDropdown);

	const isSearching = searchValue.trim().length > 0;

	// Archived lists are only fetched while the panel is open (lazy — avoids a page-load request).
	const { data: archivedData, isFetching: archivedFetching } = useGetLeanListsQuery(
		{ page: 1, limit: ARCHIVED_FETCH_LIMIT, archived: true },
		{ skip: !showDropdown }
	);
	const archivedLists = archivedData?.data ?? [];
	const archivedCount = archivedData?.meta?.itemCount ?? 0;

	const closeDropdown = useCallback(() => {
		setShowDropdown(false);
		setArchivedExpanded(false);
	}, []);

	const handleSelectItem = (selected: Partial<List>) => {
		handleSelect(selected);
		closeDropdown();
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
		handleSearch?.(e.target.value);
		setShowDropdown(true);
	};

	const handleSearchReset = () => {
		setSearchValue("");
		handleSearch?.("");
	};

	// Close on outside click.
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!(target instanceof HTMLElement)) {
				return;
			}

			if (
				triggerRef.current &&
				!triggerRef.current.contains(target) &&
				!document.getElementById("ListPickerDropdown-panel")?.contains(target)
			) {
				closeDropdown();
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [closeDropdown]);

	// Keep the panel anchored to the trigger on scroll / resize.
	useEffect(() => {
		if (!showDropdown) {
			return;
		}

		const updatePosition = () => {
			if (triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();

				setPanelPos({ top: rect.bottom + 4, left: rect.left });
			}
		};

		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [showDropdown]);

	// Infinite scroll for the active list.
	useEffect(() => {
		if (!showDropdown) {
			return;
		}

		const container = bodyRef.current;

		const maybeFetchMore = () => {
			if (isFetching || currentPage >= totalPages || !container) {
				return;
			}

			const reachedBottom =
				container.scrollTop + container.clientHeight >= container.scrollHeight - 24;

			if (reachedBottom) {
				handleFetchMore();
			}
		};

		const onScroll = debounce(maybeFetchMore, 200);

		container?.addEventListener("scroll", onScroll);

		return () => container?.removeEventListener("scroll", onScroll);
	}, [showDropdown, isFetching, currentPage, totalPages, handleFetchMore]);

	const toggleDropdown = () => {
		setShowDropdown((prev) => !prev);
	};

	const isRowSelected = (item: Partial<List>) => !!selectedItem && item.name === selectedItem;

	const renderBody = () => {
		if (isLoading || isListLoading) {
			return (
				<div className="py-8">
					<Loader />
				</div>
			);
		}

		const hasActive = data.length > 0;

		return (
			<>
				{hasActive ? (
					data.map((item) => (
						<ListPickerRow
							key={`active-${item.id}`}
							isSelected={isRowSelected(item)}
							list={item}
							onSelect={handleSelectItem}
						/>
					))
				) : (
					<p className="py-6 px-3 text-sm text-center text-text-meta">{ts("noResults")}</p>
				)}

				{isFetching && !isLoading && (
					<div className="flex items-center justify-center py-3">
						<Spinner color="#6B7280" />
					</div>
				)}

				{/* Archived section — hidden while searching */}
				{!isSearching && archivedCount > 0 && (
					<>
						<button
							className="w-full flex items-center justify-between h-9 px-3 mt-1 border-t border-border bg-selected-item text-xs font-medium text-text-meta hover:bg-gray-100 duration-200"
							data-test="list-picker-archived-toggle"
							type="button"
							onClick={() => setArchivedExpanded((prev) => !prev)}
						>
							<span>{ts("archived", { count: archivedCount })}</span>
							<Icon className="w-3 h-3" icon={archivedExpanded ? faChevronUp : faChevronDown} />
						</button>

						{archivedExpanded && (
							<>
								{archivedFetching ? (
									<div className="flex items-center justify-center py-3">
										<Spinner color="#6B7280" />
									</div>
								) : (
									archivedLists.map((item) => (
										<ListPickerRow
											key={`archived-${item.id}`}
											isArchived
											isSelected={isRowSelected(item)}
											list={item}
											onSelect={handleSelectItem}
										/>
									))
								)}
							</>
						)}
					</>
				)}
			</>
		);
	};

	return (
		<div ref={triggerRef} className="relative">
			<button
				className={classNames(
					"input-styles inline-flex items-center gap-2 w-fit max-w-[260px] pl-3 pr-3 shadow-sm !h-[32px] !rounded-[10px] text-xs font-semibold text-gray-900",
					showDropdown ? "!border-brand-light" : ""
				)}
				data-test={testId}
				type="button"
				onClick={toggleDropdown}
			>
				<Icon className="w-3.5 h-3.5 shrink-0 text-text-meta" icon={faList} />
				<span className="truncate text-gray-900">{label}</span>
				<Icon
					className="w-3.5 h-3.5 shrink-0 text-text-meta"
					icon={showDropdown ? faChevronUp : faChevronDown}
				/>
			</button>

			{showDropdown &&
				createPortal(
					<div
						ref={panelRef}
						className="bg-white border border-border rounded-xl shadow-xl overflow-hidden"
						id="ListPickerDropdown-panel"
						style={{
							position: "fixed",
							top: panelPos.top,
							left: panelPos.left,
							width: PANEL_WIDTH,
							zIndex: 99999,
						}}
					>
						<div className="px-3 py-2.5 border-b border-border">
							<div className="relative flex items-center">
								<Icon
									className="absolute left-2.5 w-3.5 h-3.5 text-placeholder pointer-events-none"
									icon={faSearch}
								/>
								<input
									className="w-full h-8 pl-8 pr-8 text-sm rounded-md border border-border-light bg-selected-item text-gray-900 placeholder:text-placeholder focus:border-brand-light focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-light/15 duration-200"
									name="list-picker-search"
									placeholder={ts("searchPlaceholder")}
									type="text"
									value={searchValue}
									onChange={handleSearchChange}
								/>
								{!!searchValue && (
									<button
										aria-label="clear-search"
										className="absolute right-2 flex items-center justify-center text-placeholder hover:text-gray-900 duration-200"
										type="button"
										onClick={handleSearchReset}
									>
										<Icon className="w-3 h-3" icon={faXmark} />
									</button>
								)}
							</div>
						</div>

						<div
							ref={bodyRef}
							className={classNames(
								"max-h-[420px] overflow-y-auto bb-scrollbar-darker py-1",
								isLoading || isListLoading ? "min-h-[120px]" : ""
							)}
						>
							{renderBody()}
						</div>
					</div>,
					document.body
				)}
		</div>
	);
};
